import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Loader2, TicketCheck, RefreshCw, Mail, Clock, Check, ChevronRight,
  MessageSquare, LifeBuoy,
} from "lucide-react";

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

/**
 * AdminSupportTicketsCard — inbox for tickets filed via the support
 * chat bubble. Shows the full transcript so admin can see exactly what
 * the user asked and what Claude replied before they escalated.
 */
export default function AdminSupportTicketsCard() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("open");
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set("status", statusFilter);
      params.set("limit", "200");
      const r = await axios.get(`${API}/admin/support/tickets?${params}`, {
      });
      setTickets(r.data.tickets || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load tickets");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const close = async (ticket_id) => {
    if (!window.confirm("Mark this ticket as closed?")) return;
    try {
      await axios.post(`${API}/admin/support/tickets/${ticket_id}/close`, {});
      toast.success("Ticket closed");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to close");
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 space-y-4" data-testid="admin-support-tickets">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-heading text-xl text-tbc-navy flex items-center gap-2">
            <LifeBuoy className="w-5 h-5 text-tbc-gold" /> Support Tickets
          </h2>
          <p className="text-sm text-gray-500 font-body">
            Filed via the chat bubble when the AI couldn&apos;t resolve an issue.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {["open", "closed"].map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors capitalize ${
                statusFilter === s
                  ? "bg-tbc-navy text-white border-tbc-navy"
                  : "bg-white text-gray-600 border-gray-200 hover:border-tbc-navy"
              }`}
              data-testid={`support-status-${s}`}
            >
              {s}
            </button>
          ))}
          <Button variant="outline" onClick={load} data-testid="support-tickets-refresh">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {loading && tickets.length === 0 ? (
        <div className="py-12 flex items-center justify-center text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading tickets…
        </div>
      ) : tickets.length === 0 ? (
        <div className="py-12 text-center text-gray-500 font-body">
          🎉 No {statusFilter} tickets. Community is running smoothly.
        </div>
      ) : (
        <div className="space-y-3">
          {tickets.map((t) => {
            const isOpen = expanded === t.ticket_id;
            return (
              <div
                key={t.ticket_id}
                className="border border-gray-100 rounded-xl overflow-hidden"
                data-testid={`support-ticket-${t.ticket_id}`}
              >
                <button
                  onClick={() => setExpanded(isOpen ? null : t.ticket_id)}
                  className="w-full text-left p-4 hover:bg-gray-50 transition-colors flex items-start gap-3"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-heading text-sm text-tbc-navy">{t.subject}</p>
                      <Badge
                        className={t.status === "open" ? "bg-red-100 text-red-700 border-red-200 border" : "bg-emerald-100 text-emerald-700 border-emerald-200 border"}
                      >
                        {t.status}
                      </Badge>
                    </div>
                    <div className="text-xs text-gray-500 mt-1 flex items-center gap-3 flex-wrap">
                      <span className="flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        <a href={`mailto:${t.user_email}`} className="hover:underline" onClick={(e) => e.stopPropagation()}>
                          {t.user_email}
                        </a>
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {formatDate(t.created_at)}
                      </span>
                      {t.transcript?.length > 0 && (
                        <span className="flex items-center gap-1">
                          <MessageSquare className="w-3 h-3" />
                          {t.transcript.length} chat msgs
                        </span>
                      )}
                    </div>
                  </div>
                  <ChevronRight
                    className={`w-4 h-4 text-gray-400 flex-shrink-0 mt-1 transition-transform ${isOpen ? "rotate-90" : ""}`}
                  />
                </button>

                {isOpen && (
                  <div className="border-t border-gray-100 bg-gray-50 p-4 space-y-3" data-testid={`support-ticket-detail-${t.ticket_id}`}>
                    <div>
                      <p className="text-xs uppercase tracking-widest text-gray-500 mb-1">Description</p>
                      <p className="text-sm text-gray-800 whitespace-pre-wrap font-body">
                        {t.description}
                      </p>
                    </div>

                    {t.transcript?.length > 0 && (
                      <div>
                        <p className="text-xs uppercase tracking-widest text-gray-500 mb-2">Chat transcript</p>
                        <div className="space-y-2 max-h-64 overflow-y-auto bg-white rounded-lg p-3 border border-gray-100">
                          {t.transcript.map((m, i) => (
                            <div key={`transcript-${i}-${m.role}`} className={`text-sm ${m.role === "user" ? "text-tbc-navy" : "text-gray-700"}`}>
                              <strong className="font-heading capitalize">{m.role}:</strong>{" "}
                              <span className="whitespace-pre-wrap font-body">{m.content}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="flex gap-2 justify-end pt-2">
                      <a
                        href={`mailto:${t.user_email}?subject=Re: ${encodeURIComponent(t.subject)}`}
                        className="inline-flex items-center gap-1 text-xs px-3 py-2 rounded-full border border-tbc-navy/30 text-tbc-navy hover:bg-tbc-navy/5"
                        data-testid={`support-ticket-reply-${t.ticket_id}`}
                      >
                        <Mail className="w-3 h-3" /> Reply by email
                      </a>
                      {t.status === "open" && (
                        <Button
                          size="sm"
                          onClick={() => close(t.ticket_id)}
                          className="bg-tbc-navy hover:bg-tbc-navy/90 text-white"
                          data-testid={`support-ticket-close-${t.ticket_id}`}
                        >
                          <Check className="w-3 h-3 mr-1" /> Mark closed
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
